import axios from "axios";
import apiUrls from "src/api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IAppLoaderAction } from "src/common/state/loaderHandleMiddleware";
import { UserFormResults } from "../components/chatState";
import { IGptConversation } from "src/model/model";
import { RootState } from "src/common/state/store";
import { IMedia } from "src/model/unityObject";
import { getMedia } from "src/features/gallery/state/gallerySlice";
import { syncUserWithServer } from "src/features/profile/state/profileSlice";

interface IWixarAIStore {
  activeGptConversation?: IGptConversation | null;
  gptConversations: IGptConversation[];
  idOfLatestProjectGroupCursedByAi: number | null;
  scenarioHelperContextPdfs: IMedia[];
  chosenLlmProvider: string;
  lastAiModuleConversationId: number | null;
  lastAiModuleOutputIndex: number | null;
  lastAiModuleGenerateBlockadeImages: boolean;
}

const initialState: IWixarAIStore = {
  activeGptConversation: null,
  gptConversations: [],
  idOfLatestProjectGroupCursedByAi: null,
  scenarioHelperContextPdfs: [],
  chosenLlmProvider: "openai",
  lastAiModuleConversationId: null,
  lastAiModuleOutputIndex: null,
  lastAiModuleGenerateBlockadeImages: false,
};

export const fetchGptConversations = createAsyncThunk(
  "gpt/fetchGptConversations",
  // eslint-disable-next-line no-empty-pattern
  async ({}: Record<string, string> & IAppLoaderAction) => {
    const response = (await axios.get)<Array<IGptConversation>>(
      `${apiUrls.gpt.findAllWithNestedStuff}`,
    );
    return { data: (await response).data };
  },
);

export const createChat = createAsyncThunk(
  "gpt/createChat",
  async (
    {
      userPrompt,
      userFormResults,
      sourceMediasWithContext = undefined,
      sourceMedias,
      chosenLlmProvider,
      cb,
      cbOnRequestEnd,
    }: {
      userPrompt: string;
      userFormResults: UserFormResults;
      sourceMediasWithContext?: number[];
      sourceMedias;
      chosenLlmProvider: string;
      cb?: (chunk: string) => void;
      cbOnRequestEnd?: () => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.createChat, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
            userFormResults,
            sourceMediasWithContext,
            service: chosenLlmProvider,
          },
        }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (cb) {
            cb(chunk);
          }
        }
      }

      const timestamp = new Date().toISOString();

      const newConversation: IGptConversation = {
        conversation_metadata: {
          model: "gpt-4",
          timestamp,
          userPrompts: [userPrompt],
          userFormResults: userFormResults,
          source_media_with_context: [],
        },
        conversation_data: {
          output: [botMessage],
        },
        createdAt: timestamp,
        updatedAt: timestamp,
        source_media_with_context: sourceMedias.filter((sm) =>
          sourceMediasWithContext.includes(Number(sm.id)),
        ),
      };

      if (cbOnRequestEnd) {
        cbOnRequestEnd();
      }

      return newConversation;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (createChat)");
    }
  },
);

export const updateChat = createAsyncThunk(
  "gpt/updateChat",
  async (
    {
      userPrompt,
      conversationId,
      cb,
    }: {
      userPrompt: string;
      conversationId: number;
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.updateChat, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ userPrompt, conversationId }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (cb) {
            cb(chunk);
          }
        }
      }

      return { userPrompt, botResponse: botMessage };
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (updateChat)");
    }
  },
);

export const resetChat = createAsyncThunk(
  "gpt/resetChat",
  async ({ cb }: { cb(): any } & IAppLoaderAction) => {
    return await axios
      .put(apiUrls.gpt.resetChat)
      .then((res) => {
        cb();
        return res;
      })
      .catch((e) => {
        console.error("resetChat did an oopsie: ", e);
        return e;
      });
  },
);

export const askWixarpedia = createAsyncThunk(
  "gpt/askWixarpedia",
  async (
    {
      userPrompt,
      cb,
    }: {
      userPrompt: string;
      cb?: (chunk: string) => void;
    },
    { rejectWithValue },
  ) => {
    const jwt = localStorage.getItem("wixar.auth.jwt");
    try {
      const response = await fetch(apiUrls.gpt.askWixarpedia, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          data: {
            userPrompt,
          },
        }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessage = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value);
          botMessage += chunk;

          if (cb) {
            cb(chunk);
          }
        }
      }

      return botMessage;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (askWixarpedia)");
    }
  },
);

export const updateGptConversationName = createAsyncThunk(
  "gpt/updateName",
  async (
    {
      id,
      newName,
      cb,
    }: {
      id: number;
      newName: string;
      cb?: () => void;
    },
    { rejectWithValue },
  ) => {
    try {
      return await axios
        .put(`${apiUrls.gpt.gptConversations}/${id}`, {
          name: newName,
        })
        .then((res) => {
          if (cb) {
            cb();
          }
          return res.data;
        })
        .catch((e) => {
          console.error("Error in chatbotSlice.ts :", e);
          throw e;
        });
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (updateGptConversationName)");
    }
  },
);

export const deleteGptConversation = createAsyncThunk(
  "gpt/deleteName",
  async (
    {
      id,
      cb,
    }: {
      id: number;
      cb?: () => void;
    },
    { rejectWithValue },
  ) => {
    try {
      return await axios
        .delete(`${apiUrls.gpt.gptConversations}/${id}`)
        .then((res) => {
          if (cb) {
            cb();
          }
          return res.data;
        })
        .catch((e) => {
          console.error("Error in chatbotSlice.ts 299:", e);
          throw e;
        });
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (deleteGptConversation)");
    }
  },
);

export const generateProjectGroupFromAiGraph = createAsyncThunk(
  "gpt/generateProjectGroupFromAiGraph",
  async (
    {
      conversationId,
      name,
      generateBlockadeImages = false,
      relatedGptOutputIndex = undefined,
      cb,
    }: {
      conversationId: number;
      name: string;
      generateBlockadeImages?: boolean;
      relatedGptOutputIndex?: number;
      cb?: () => void;
    } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const jwt = localStorage.getItem("wixar.auth.jwt");
      if (!jwt) {
        throw new Error("JWT token not found in localStorage.");
      }

      const response = await fetch(`${apiUrls.gpt.generateTemplate}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name,
          conversationId,
          generateBlockadeImages,
          relatedGptOutputIndex,
          createAsProjectGroup: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch the API");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Readable stream is not supported");
      }

      const decoder = new TextDecoder();
      let result = "";

      const readNextChunk = async () => {
        const { done, value } = await reader.read();

        if (done) return; // End recursion when the stream is done

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          chunk
            .trim()
            .split("\n")
            .forEach((line) => {
              if (!line) return;

              try {
                const parsed = JSON.parse(line);
                if (parsed.status === "complete") {
                  result = parsed.data;
                } else if (parsed.error) {
                  throw new Error(parsed.error);
                }
              } catch (error) {
                console.error("Error parsing chunk:", error, "Raw chunk:", line);
              }
            });
        }

        // Recursively read the next chunk
        await readNextChunk();
      };

      await readNextChunk(); // Start the recursive read

      if (cb) cb();

      return result; // Return final result
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (generateProjectGroupFromAiGraph)");
    }
  },
);

export const fetchAndUpdateSpecificConversation = createAsyncThunk(
  "gpt/fetchAndUpdateSpecificConversation",
  async ({ conversationId }: { conversationId: number }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrls.gpt.gptConversations}/${conversationId}`);

      if (!response.data) {
        throw new Error("No conversation data returned");
      }

      return response.data;
    } catch (error) {
      console.error("API call failed:", error);
      return rejectWithValue("API call failed (fetchAndUpdateSpecificConversation)");
    }
  },
);

export const chatbotSlice = createSlice({
  name: "chatbot",
  initialState,
  reducers: {
    setActiveGptConversation: (state: IWixarAIStore, action) => {
      state.activeGptConversation = action.payload;
      state.chosenLlmProvider = action.payload?.conversation_metadata?.model.includes("mistral")
        ? "mistral"
        : "openai";
    },
    resetActiveGptConversation: (state: IWixarAIStore) => {
      state.activeGptConversation = null;
    },
    setIdOfLatestProjectGroupCursedByAi: (state: IWixarAIStore, action) => {
      state.idOfLatestProjectGroupCursedByAi = action.payload;
    },
    resetIdOfLatestProjectGroupCursedByAi: (state: IWixarAIStore) => {
      state.idOfLatestProjectGroupCursedByAi = null;
    },
    addScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextPdfs.push(action.payload);
    },
    removeScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextPdfs = state.scenarioHelperContextPdfs.filter(
        (pdf) => pdf.id !== action.payload,
      );
    },
    setScenarioHelperContextPdf: (state: IWixarAIStore, action) => {
      state.scenarioHelperContextPdfs = action.payload;
    },
    resetScenarioHelperContextPdfs: (state: IWixarAIStore) => {
      state.scenarioHelperContextPdfs = [];
    },
    setChosenLlmProvider: (state: IWixarAIStore, action) => {
      state.chosenLlmProvider = action.payload;
    },
    setLastAiModuleContext: (
      state: IWixarAIStore,
      action: {
        payload: {
          conversationId: number;
          relatedGptOutputIndex: number;
          generateBlockadeImages: boolean;
        };
      },
    ) => {
      state.lastAiModuleConversationId = action.payload.conversationId;
      state.lastAiModuleOutputIndex = action.payload.relatedGptOutputIndex;
      state.lastAiModuleGenerateBlockadeImages = action.payload.generateBlockadeImages;
    },
    resetLastAiModuleContext: (state: IWixarAIStore) => {
      state.lastAiModuleConversationId = null;
      state.lastAiModuleOutputIndex = null;
      state.lastAiModuleGenerateBlockadeImages = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGptConversations.fulfilled, (state, { payload }) => {
      state.gptConversations = payload.data;
    });
    builder.addCase(createChat.fulfilled, (state, { payload }) => {
      state.activeGptConversation = payload;
      state.gptConversations.push(payload);
    });
    builder.addCase(updateChat.fulfilled, (state, { payload }) => {
      if (state.activeGptConversation) {
        state.activeGptConversation.conversation_metadata.userPrompts.push(payload.userPrompt);
        state.activeGptConversation.conversation_data.output.push(payload.botResponse);
      }
    });
    builder.addCase(resetChat.fulfilled, (state, { payload }) => {
      state.activeGptConversation = null;
      state.gptConversations = payload.data;
    });
    builder.addCase(updateGptConversationName.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].map((conversation) => {
        if (Number(conversation.id) === Number(payload?.id)) {
          return payload;
        }
        return conversation;
      });
      state.gptConversations = updatedGptConversationArray;
    });
    builder.addCase(deleteGptConversation.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].filter((conversation) => {
        if (Number(conversation.id) === Number(payload?.id)) {
          return false;
        }
        return true;
      });
      state.gptConversations = updatedGptConversationArray;
      if (Number(state.activeGptConversation?.id) === Number(payload?.id)) {
        state.activeGptConversation = null;
      }
    });
    builder.addCase(fetchAndUpdateSpecificConversation.fulfilled, (state, { payload }) => {
      const updatedGptConversationArray = [...state.gptConversations].map((conversation) => {
        if (Number(conversation.id) === Number(payload.id)) {
          return payload;
        }
        return conversation;
      });

      state.gptConversations = updatedGptConversationArray;

      if (state.activeGptConversation?.id === payload.id) {
        state.activeGptConversation = payload;
      }
    });
    builder.addCase(getMedia.fulfilled, (state, { payload }) => {
      const updatedMedia = payload;
      state.scenarioHelperContextPdfs = state.scenarioHelperContextPdfs.map((pdf) => {
        return Number(pdf.id) === Number(updatedMedia.id) ? updatedMedia : pdf;
      });
    });
    builder.addCase(syncUserWithServer.fulfilled, (state, { payload }) => {
      const updatedGptConversation = payload.active_gpt_conversation;
      state.activeGptConversation = updatedGptConversation;
      state.scenarioHelperContextPdfs = updatedGptConversation?.source_media_with_context ?? [];
    });
  },
});

export const chatbotReducer = chatbotSlice.reducer;

export const getActiveGptConversation = (state: RootState) => state.chatbot.activeGptConversation;
export const getIdOfLatestProjectGroupCursedByAi = (state: RootState) =>
  state.chatbot.idOfLatestProjectGroupCursedByAi;
export const getScenarioHelperContextPdfs = (state: RootState) =>
  state.chatbot.scenarioHelperContextPdfs;
export const getChosenLlmProvider = (state: RootState) => state.chatbot.chosenLlmProvider;
export const getLastAiModuleContext = (state: RootState) => ({
  conversationId: state.chatbot.lastAiModuleConversationId,
  outputIndex: state.chatbot.lastAiModuleOutputIndex,
  generateBlockadeImages: state.chatbot.lastAiModuleGenerateBlockadeImages,
});

export const getGptConversationsSortedByRecency = (state: RootState) => {
  const sortedConversations = [...state.chatbot.gptConversations].sort((a, b) => {
    const timestampA = new Date(a.updatedAt).getTime();
    const timestampB = new Date(b.updatedAt).getTime();
    return timestampB - timestampA;
  });

  return sortedConversations;
};

export const {
  setActiveGptConversation,
  resetActiveGptConversation,
  setIdOfLatestProjectGroupCursedByAi,
  resetIdOfLatestProjectGroupCursedByAi,
  addScenarioHelperContextPdf,
  setScenarioHelperContextPdf,
  removeScenarioHelperContextPdf,
  resetScenarioHelperContextPdfs,
  setChosenLlmProvider,
  setLastAiModuleContext,
  resetLastAiModuleContext,
} = chatbotSlice.actions;
